<template>
  <v-container class="public-onboarding fill-height" fluid>
    <v-row align="center" justify="center" style="min-width: 80vw;">
      <v-col cols="12" sm="10" md="8" lg="6" xl="5">
        <v-card class="pa-6 elevation-8">
          <!-- Logo -->
          <div class="text-center mb-6">
            <img src="@/assets/images/logo.svg" alt="Logo" style="max-width: 200px;" />
          </div>

          <h1 class="text-h5 text-center mb-2">{{ $t('onboarding.title') }}</h1>

          <!-- Enrollment Canceled -->
          <div v-if="enrollmentCanceled" class="text-center py-8">
            <v-icon size="80" color="red" class="mb-4">mdi-cancel</v-icon>
            <h2 class="text-h6 mb-4">{{ $t('onboarding.enrollment-canceled-title') }}</h2>
            <p class="text--secondary">{{ $t('onboarding.enrollment-canceled-message') }}</p>
          </div>

          <!-- Enrollment Failed -->
          <div v-else-if="enrollmentFailed" class="text-center py-8">
            <v-icon size="80" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
            <h2 class="text-h6 mb-4">{{ $t('onboarding.enrollment-failed-title') }}</h2>
            <p class="text--secondary">{{ $t('onboarding.enrollment-failed-message') }}</p>
          </div>

          <template v-else>
            <!-- Title -->
            <p class="text-center text--secondary mb-6">{{ $t('onboarding.subtitle') }}</p>

            <!-- Error Banner -->
            <v-alert v-if="globalError" type="error" dense outlined class="mb-4">
              {{ globalError }}
            </v-alert>

            <!-- Token Expired -->
            <div v-if="tokenExpired" class="text-center py-8">
              <v-icon size="80" color="error" class="mb-4">mdi-clock-alert-outline</v-icon>
              <h2 class="text-h6 mb-4">{{ $t('onboarding.token-expired') }}</h2>
              <p class="text--secondary">{{ $t('onboarding.token-expired-message') }}</p>
            </div>

            <!-- Loading -->
            <div v-else-if="initialLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" :size="60" :width="6" class="mb-4"></v-progress-circular>
              <p class="text--secondary">{{ $t('loading') }}</p>
            </div>

            <!-- Stepper -->
            <template v-else>
              <!-- Welcome screen (Step 0) -->
              <div v-if="currentStep === 0" class="welcome-screen text-center py-8">
                <v-icon size="80" color="primary" class="mb-4">mdi-wallet-outline</v-icon>

                <h1 class="text-h4 mb-4">{{ $t('onboarding.welcome.title') }}</h1>
                <p class="text-body-1 text--secondary mb-6" style="max-width: 600px; margin: 0 auto;">
                  {{ $t('onboarding.welcome.description') }}
                </p>

                <!-- Process explanation -->
                <v-card outlined class="mb-6" style="max-width: 700px; margin: 0 auto;">
                  <v-card-title class="justify-center">
                    <v-icon left color="primary">mdi-information-outline</v-icon>
                    {{ $t('onboarding.welcome.process-title') }}
                  </v-card-title>
                  <v-card-text>
                    <v-timeline dense align-top>
                      <v-timeline-item small color="primary">
                        <div class="text-left">
                          <div class="text-subtitle-2">{{ $t('onboarding.step1.title') }}</div>
                          <div class="text-caption text--secondary">{{ $t('onboarding.welcome.step1-desc') }}</div>
                        </div>
                      </v-timeline-item>
                      <v-timeline-item small color="primary">
                        <div class="text-left">
                          <div class="text-subtitle-2">{{ $t('onboarding.step2.title') }}</div>
                          <div class="text-caption text--secondary">{{ $t('onboarding.welcome.step2-desc') }}</div>
                        </div>
                      </v-timeline-item>
                      <v-timeline-item small color="primary">
                        <div class="text-left">
                          <div class="text-subtitle-2">{{ $t('onboarding.step3.title') }}</div>
                          <div class="text-caption text--secondary">{{ $t('onboarding.welcome.step3-desc') }}</div>
                        </div>
                      </v-timeline-item>
                      <v-timeline-item small color="success">
                        <div class="text-left">
                          <div class="text-subtitle-2">{{ $t('onboarding.step4.title') }}</div>
                          <div class="text-caption text--secondary">{{ $t('onboarding.welcome.step4-desc') }}</div>
                        </div>
                      </v-timeline-item>
                    </v-timeline>

                    <v-alert type="info" dense outlined class="mt-4">
                      <v-icon left small>mdi-clock-outline</v-icon>
                      {{ $t('onboarding.welcome.duration') }}
                    </v-alert>
                  </v-card-text>
                </v-card>

                <v-btn
                  x-large
                  color="primary"
                  :loading="stepLoading"
                  @click="startOnboarding"
                >
                  <v-icon left>mdi-rocket-launch</v-icon>
                  {{ $t('onboarding.welcome.start-button') }}
                </v-btn>
              </div>

              <!-- Main workflow steps (1-4) -->
              <div v-else>
                <!-- Progress bar -->
                <v-progress-linear :value="progressPercent" color="primary" height="8" rounded class="mb-6"></v-progress-linear>

                <v-stepper v-model="currentStep" alt-labels flat class="elevation-0">
                <v-stepper-header>
                  <v-stepper-step :complete="currentStep > 1" step="1">
                    {{ $t('onboarding.step1.title') }}
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step :complete="currentStep > 2" step="2">
                    {{ $t('onboarding.step2.title') }}
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step :complete="currentStep > 3" step="3">
                    {{ $t('onboarding.step3.title') }}
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step :complete="currentStep > 4" step="4">
                    {{ $t('onboarding.step4.title') }}
                  </v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                  <!-- Step 1: Validate Info -->
                  <v-stepper-content step="1">
                    <step-one
                      :enrollment="enrollment"
                      :loading="stepLoading"
                      @next="handleStep1Next"
                    />
                  </v-stepper-content>

                  <!-- Step 2: IBAN Input (Stripe) -->
                  <v-stepper-content step="2">
                    <step-two
                      :loading="stepLoading"
                      :enrollment="enrollment"
                      :existing-payment-method-id="stripePaymentMethodId"
                      :payment-method-details="enrollment && enrollment.paymentMethod"
                      @next="handleStep2Next"
                      @back="currentStep = 1"
                    />
                  </v-stepper-content>

                  <!-- Step 3: Mandate Preview -->
                  <v-stepper-content step="3">
                    <step-three
                      :mandate-html="mandateHtml"
                      :loading="stepLoading"
                      @next="handleStep3Next"
                      @back="handleStep3Back"
                    />
                  </v-stepper-content>

                  <!-- Step 4: Success -->
                  <v-stepper-content step="4">
                    <step-four
                      :mandate-reference="mandateReference"
                      :mandate-url="mandateUrl"
                    />
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
              </div>
            </template>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import StripeService from '@service/StripeService'
import StepOne from './Steps/StepOne.vue'
import StepTwo from './Steps/StepTwo.vue'
import StepThree from './Steps/StepThree.vue'
import StepFour from './Steps/StepFour.vue'

export default {
  name: 'PublicOnboarding',

  components: {
    StepOne,
    StepTwo,
    StepThree,
    StepFour
  },

  data() {
    return {
      currentStep: 0, // 0 = welcome screen, 1-4 = actual steps
      initialLoading: true,
      stepLoading: false,
      globalError: null,
      tokenExpired: false,
      enrollmentCanceled: false,
      enrollmentFailed: false,
      token: null,
      enrollment: null,
      stripePaymentMethodId: null,
      mandateHtml: null,
      mandateContentHash: null,
      mandateSnapshotData: null,
      mandateReference: null,
      mandateUrl: null,
      // SetupIntent for SEPA confirmation
      sepaClientSecret: null,
      stripe: null
    }
  },

  computed: {
    progressPercent() {
      // Don't show progress for step 0 (welcome)
      if (this.currentStep === 0) return 0
      return (this.currentStep / 4) * 100
    }
  },

  created() {
    // Apply locale from URL immediately (before any rendering)
    const localeParam = this.$route.query.locale
    if (localeParam && ['en', 'fr', 'nl'].includes(localeParam)) {
      this.$i18n.locale = localeParam
    }
  },

  mounted() {
    this.loadEnrollment()
  },

  methods: {
    async loadEnrollment() {
      this.initialLoading = true
      this.globalError = null
      this.tokenExpired = false
      this.enrollmentCanceled = false
      this.enrollmentFailed = false

      try {
        // Get token from URL query
        this.token = this.$route.query.token

        if (!this.token) {
          throw new Error(this.$t('onboarding.error.no-token'))
        }

        // Fetch enrollment data
        const data = await PaymentOnboardingRepository.getEnrollmentByToken(this.token)
        this.enrollment = data

        // Check if token is still valid
        if (!data.isTokenValid) {
          this.tokenExpired = true
          return
        }

        // Check if enrollment is canceled
        if (data.status === 'canceled') {
          this.enrollmentCanceled = true
          return
        }

        // Check if enrollment has failed
        if (data.status === 'failed') {
          this.enrollmentFailed = true
          return
        }

        // Check status and redirect to appropriate step
        if (data.status === 'draft' || data.status === 'sent') {
          // New enrollment, show welcome screen
          this.currentStep = 0
        } else if (data.status === 'visited') {
          // User clicked "Start" but hasn't validated info yet
          this.currentStep = 1
        } else if (data.status === 'validated') {
          // User validated info, ready for IBAN
          this.currentStep = 2
          // If payment method already exists (from previous attempt), restore it
          if (data.stripePaymentMethodId) {
            this.stripePaymentMethodId = data.stripePaymentMethodId
          }
        } else if (data.status === 'mandate_previewed') {
          // Restore Step 3 data from backend
          // Need to re-fetch the mandate preview to get HTML, contentHash and snapshotData
          this.currentStep = 3
          this.stripePaymentMethodId = data.stripePaymentMethodId

          // Re-fetch mandate preview with signatory information
          try {
            const previewParams = {
              stripePaymentMethodId: data.stripePaymentMethodId,
              locale: this.$i18n?.locale || data.locale || 'fr'
            }

            // Add signatory info if available in enrollment data
            if (data.firstName) previewParams.signatoryFirstName = data.firstName
            if (data.lastName) previewParams.signatoryLastName = data.lastName

            const mandateData = await PaymentOnboardingRepository.previewMandate(this.token, previewParams)
            this.mandateHtml = mandateData.mandateHtml
            this.mandateContentHash = mandateData.contentHash
            this.mandateSnapshotData = mandateData.snapshotData
          } catch (previewErr) {
            // Fallback to step 2 if preview fails
            this.currentStep = 2
          }
        } else if (data.status === 'completed' || data.status === 'signed' || data.status === 'converted') {
          // Restore Step 4 data from backend
          // Status 'converted' means enrollment was converted to organization, keep them on success page
          this.currentStep = 4
          this.stripePaymentMethodId = data.stripePaymentMethodId
          this.mandateReference = data.mandateReference
          this.mandateUrl = data.mandateUrl
        }

      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          this.tokenExpired = true
        } else {
          this.globalError = err.response?.data?.message || err.message || this.$t('onboarding.error.load-failed')
        }
      } finally {
        this.initialLoading = false
      }
    },

    async handleStep1Next(formData) {
      this.stepLoading = true
      this.globalError = null

      try {
        await PaymentOnboardingRepository.validateEnrollment(this.token, formData)

        // Reload enrollment to get updated data
        this.enrollment = await PaymentOnboardingRepository.getEnrollmentByToken(this.token)

        this.currentStep = 2
      } catch (err) {
        const errorCode = err.response?.data?.error
        if (errorCode === 'onboarding.address-required') {
          this.globalError = this.$t('onboarding.address-required')
        } else if (errorCode && errorCode.startsWith('onboarding.')) {
          // Handle known onboarding error codes
          this.globalError = this.$t(errorCode) || err.response?.data?.message
        } else {
          this.globalError = err.response?.data?.message || err.message || this.$t('onboarding.error.validation-failed')
        }
      } finally {
        this.stepLoading = false
      }
    },

    async handleStep2Next(data) {
      this.stepLoading = true
      this.globalError = null

      try {
        // Support both old format (string) and new format (object)
        const paymentMethodId = typeof data === 'string' ? data : data.paymentMethodId
        const { firstName, lastName, email } = typeof data === 'object' ? data : {}

        // If we have signer information, save it to the enrollment
        if (firstName && lastName && email) {
          await PaymentOnboardingRepository.validateEnrollment(this.token, {
            firstName,
            lastName,
            email
          })
        }

        this.stripePaymentMethodId = paymentMethodId

        // 1. Créer le SetupIntent côté backend (lié au Customer)
        const setupIntentRes = await PaymentOnboardingRepository.createSetupIntent(this.token, {
          paymentMethodType: 'sepa_debit'
        })
        this.sepaClientSecret = setupIntentRes?.clientSecret || setupIntentRes?.client_secret

        // Get mandate preview with signatory information
        const previewParams = {
          stripePaymentMethodId: paymentMethodId,
          locale: this.$i18n?.locale || 'fr'
        }

        // Add signatory info if available
        if (firstName) previewParams.signatoryFirstName = firstName
        if (lastName) previewParams.signatoryLastName = lastName

        const mandateData = await PaymentOnboardingRepository.previewMandate(this.token, previewParams)

        // Store mandate preview data (contentHash and snapshotData for acceptance)
        this.mandateHtml = mandateData.mandateHtml
        this.mandateContentHash = mandateData.contentHash
        this.mandateSnapshotData = mandateData.snapshotData
        this.currentStep = 3
      } catch (err) {
        this.globalError = err.response?.data?.message || err.message || this.$t('onboarding.error.iban-failed')
      } finally {
        this.stepLoading = false
      }
    },

    async handleStep3Back() {
      this.stepLoading = true
      this.globalError = null

      try {
        // Reload enrollment to get payment method details
        const data = await PaymentOnboardingRepository.getEnrollmentByToken(this.token)
        this.enrollment = data

        // Reset SetupIntent - a new one will be created when user submits Step 2 again
        // This is necessary because the user might enter a different IBAN
        this.sepaClientSecret = null
        this.stripePaymentMethodId = null

        this.currentStep = 2
      } catch (err) {
        this.globalError = err.response?.data?.message || err.message
      } finally {
        this.stepLoading = false
      }
    },

    async handleStep3Next() {
      this.stepLoading = true
      this.globalError = null

      try {
        // 1. Confirmer le SetupIntent avec le PM pour l'attacher au Customer
        if (this.sepaClientSecret && this.stripePaymentMethodId) {
          // Initialize Stripe if needed
          if (!this.stripe) {
            this.stripe = await StripeService.initStripe()
          }

          const { setupIntent, error } = await this.stripe.confirmSepaDebitSetup(this.sepaClientSecret, {
            payment_method: this.stripePaymentMethodId
          })

          if (error) {
            this.globalError = error.message
            return
          }

          if (setupIntent.status !== 'succeeded' && setupIntent.status !== 'processing') {
            this.globalError = `SetupIntent status: ${setupIntent.status}`
            return
          }
        }

        // 2. Finaliser l'enrollment
        const data = await PaymentOnboardingRepository.confirmEnrollment(this.token, {
          stripePaymentMethodId: this.stripePaymentMethodId,
          contentHash: this.mandateContentHash,
          snapshotData: this.mandateSnapshotData
        })

        this.mandateReference = data.mandateReference
        this.mandateUrl = data.mandateUrl
        this.sepaClientSecret = null // Reset
        this.currentStep = 4
      } catch (err) {
        this.globalError = err.response?.data?.message || err.message || this.$t('onboarding.error.confirm-failed')
      } finally {
        this.stepLoading = false
      }
    },

    async startOnboarding() {
      this.stepLoading = true
      this.globalError = null

      try {
        // Mark the enrollment as "visited" on the backend
        await PaymentOnboardingRepository.markEnrollmentVisited(this.token)

        // Reload enrollment to get updated status
        const data = await PaymentOnboardingRepository.getEnrollmentByToken(this.token)
        this.enrollment = data

        // Move to step 1
        this.currentStep = 1
      } catch (err) {
        this.globalError = err.response?.data?.message || err.message
      } finally {
        this.stepLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.public-onboarding {
  min-height: 100vh;
}
</style>
